import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { formatPrice } from '../../utilities/formatPrice';

// Renders a single post_draft chat message as a confirm card. The card
// lives inside ChatPage / ConciergeOverlay's MessageList alongside regular
// chatscope Message bubbles — branch on `item.type === 'post_draft'`
// BEFORE the chatscope Message and render this component instead.
//
// State (pending | confirmed | declined) is read from inside the draft
// JSON. The realtime subscription's dedupe-by-id replaces the message in
// place when the backend re-emits messageNew after the confirm/decline
// endpoint patches the row, so the card re-renders automatically — the
// onConfirm / onDecline handlers don't need to update local state.
export const PostDraftCard = ({ item, onConfirm, onDecline }) => {
  const intl = useIntl();
  const [submitting, setSubmitting] = useState(null); // 'confirm' | 'decline' | null

  // The draft JSON sits in `value`. If the payload is corrupted (shouldn't
  // happen — the bot writes it, server stamps state) bail to a minimal
  // fallback so the chat doesn't blank out from a throw.
  const draft = useMemo(() => {
    try {
      return JSON.parse(item?.value || '{}');
    } catch {
      return null;
    }
  }, [item?.value]);

  const confirmLabel = intl.formatMessage({ id: 'concierge.draft.confirm', defaultMessage: 'Confirm' });
  const declineLabel = intl.formatMessage({ id: 'concierge.draft.decline', defaultMessage: 'Decline' });
  const confirmedLabel = intl.formatMessage({
    id: 'concierge.draft.confirmed',
    defaultMessage: 'Listed — under review',
  });
  const declinedLabel = intl.formatMessage({ id: 'concierge.draft.declined', defaultMessage: 'Declined' });
  const viewLabel = intl.formatMessage({ id: 'concierge.draft.viewPost', defaultMessage: 'View listing' });

  if (!draft) return null;

  const {
    title,
    description,
    price,
    currencyCode,
    country,
    city,
    region,
    gender,
    condition,
    hashTags = [],
    images = [],
    videos = [],
    state = 'pending',
    postId,
  } = draft;

  const displayTitle = title || (description || '').slice(0, 60) || '—';
  const priceLine = formatPrice(price, currencyCode);
  // Location and item-meta join on the same line. City+region collapse
  // when missing; country/gender/condition are unchanged from v1.
  const locationText = [city, region].filter(Boolean).join(', ');
  const metaParts = [locationText, country, gender, condition].filter(Boolean);

  const handle = async (kind, fn) => {
    if (submitting) return;
    setSubmitting(kind);
    try {
      await fn(item.id);
    } finally {
      // On success the message gets swapped in place by the realtime
      // subscription and this component re-renders with the new state —
      // clear the local 'submitting' state so the new visual state lands
      // unguarded. On failure (no swap), the buttons re-enable so the
      // user can retry; createMessageInChat-style sagas surface the error
      // via the global snackbar.
      setSubmitting(null);
    }
  };

  return (
    <div
      className={`
        mx-2 my-3 max-w-[360px]
        rounded-lg overflow-hidden border
        border-gray-200 dark:border-[#2a2c30]
        bg-white dark:bg-[#1c1d22]
        text-black dark:text-gray-100
        shadow-sm
        ${state === 'declined' ? 'opacity-60' : ''}
      `}
      role="region"
      aria-label={displayTitle}
    >
      {/* Image preview — first image as a hero, up to 3 more as a row of
          thumbnails below. Anything beyond that is summarised as '+N'. */}
      {images.length > 0 && (
        <div>
          <img
            src={images[0]}
            alt=""
            className="w-full h-40 object-cover bg-gray-100 dark:bg-[#2a2c30]"
          />
          {images.length > 1 && (
            <div className="flex gap-1 p-1 bg-gray-50 dark:bg-[#17181c]">
              {images.slice(1, 4).map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="w-1/4 h-14 object-cover rounded"
                />
              ))}
              {images.length > 4 && (
                <div className="w-1/4 h-14 rounded bg-gray-200 dark:bg-[#2a2c30] flex items-center justify-center text-sm">
                  +{images.length - 4}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Body */}
      <div className="p-3">
        <div className="font-semibold text-base mb-1 line-clamp-1">{displayTitle}</div>
        {description && (
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2 line-clamp-3 whitespace-pre-wrap">
            {description}
          </div>
        )}

        {hashTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {hashTags.slice(0, 8).map((t, i) => (
              <span
                key={i}
                className="text-xs px-2 py-0.5 rounded-full bg-[#3a6df0]/10 text-[#3a6df0]"
              >
                #{String(t).replace(/^#/, '')}
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-3 text-xs text-gray-600 dark:text-gray-400 mb-2">
          {priceLine && <span className="font-semibold text-sm text-black dark:text-white">{priceLine}</span>}
          {metaParts.map((p, i) => (
            <span key={i}>{p}</span>
          ))}
          {videos.length > 0 && <span>🎥 video</span>}
        </div>

        {/* Footer — state-dependent */}
        {state === 'pending' && (
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => handle('confirm', onConfirm)}
              disabled={submitting !== null}
              className="
                flex-1 py-2 rounded-md text-white text-sm font-semibold
                bg-[#3a6df0] hover:bg-[#3559c0]
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              {submitting === 'confirm' ? '…' : confirmLabel}
            </button>
            <button
              type="button"
              onClick={() => handle('decline', onDecline)}
              disabled={submitting !== null}
              className="
                flex-1 py-2 rounded-md text-sm font-semibold
                border border-gray-300 dark:border-[#2a2c30]
                hover:bg-gray-50 dark:hover:bg-[#17181c]
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              {submitting === 'decline' ? '…' : declineLabel}
            </button>
          </div>
        )}

        {state === 'confirmed' && (
          <div className="flex items-center justify-between pt-2 text-sm text-[#2e7d32]">
            <span className="flex items-center gap-1">
              <CheckCircleOutlineIcon fontSize="small" />
              {confirmedLabel}
            </span>
            {postId && (
              <Link
                to={`/post/${postId}`}
                className="text-[#3a6df0] underline"
              >
                {viewLabel}
              </Link>
            )}
          </div>
        )}

        {state === 'declined' && (
          <div className="flex items-center gap-1 pt-2 text-sm text-gray-500">
            <CancelOutlinedIcon fontSize="small" />
            {declinedLabel}
          </div>
        )}
      </div>
    </div>
  );
};

export default PostDraftCard;
